import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
            <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2 font-heading">文章未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该文章可能已被删除或不存在</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const latestArticles = articles
    .filter((a) => a.id !== article.id && a.category !== article.category)
    .slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '51吃瓜网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://51chigua.example.com/article/${article.slug}`,
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `关于${article.categoryName}的最新动态在哪里可以关注？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `您可以关注51吃瓜网的${article.categoryName}分类，我们提供该人物的最新动态、深度文章和独家资讯。`,
        },
      },
      {
        '@type': 'Question',
        name: '51吃瓜网的文章更新频率是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51吃瓜网编辑团队7×24小时追踪网红圈最新动态，每天更新多篇高质量原创文章，确保您不会错过任何热门话题。',
        },
      },
      {
        '@type': 'Question',
        name: '如何订阅51吃瓜网的最新内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以在51吃瓜网首页底部输入邮箱地址订阅我们的Newsletter，第一时间获取最新网红动态和独家八卦推送。',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <div className="w-full px-4 md:px-6 pt-28 pb-16">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {article.categoryName}
              </Link>
              {article.isHot && (
                <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium">
                  热门
                </span>
              )}
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight font-heading">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount} 阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-message-2-line"></i>
                {article.commentCount >= 1000 ? `${(article.commentCount / 1000).toFixed(1)}k` : article.commentCount} 评论
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="w-full h-[300px] md:h-[450px] rounded-xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
              title={article.title}
            />
          </div>

          {/* Article Content */}
          <article
            className="article-content prose max-w-none mb-12"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Share Section */}
          <div className="flex items-center gap-3 py-6 border-t border-background-200/70 mb-12">
            <span className="text-sm text-foreground-600 font-medium">分享到：</span>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-wechat-line"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-weibo-line"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-twitter-x-line"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
              <i className="ri-link"></i>
            </button>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mb-12">
              <h3 className="text-lg font-bold text-foreground-900 mb-5 font-heading">
                <a href="#related-articles">相关文章</a>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} variant="default" />
                ))}
              </div>
            </section>
          )}

          {/* Latest Articles */}
          <section>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-foreground-900 font-heading">
                <a href="#latest-articles">最新文章</a>
              </h3>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="space-y-1">
              {latestArticles.map((a) => (
                <ArticleCard key={a.id} article={a} variant="compact" />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}